import * as React from 'react';
import { SimpleModal } from 'Src/components/SimpleModal/SimpleModal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faLock } from '@fortawesome/free-solid-svg-icons';
import './ModuleUpsellModal.scss';

export interface ModuleUpsellConfig {
  title: string;
  description: string;
  features: string[];
  contactPrompt?: string;
}

const MODULE_CONFIG: Record<string, ModuleUpsellConfig> = {
  AWARDS: {
    title: 'Awards & House Points',
    description:
      'The Awards module lets you create custom award types, grant awards to students, ' +
      'track house points, and celebrate achievements across your school.',
    features: [
      'Create custom award types (e.g. Star of the Week, House Points)',
      'Grant awards to students from events, student lists, or house views',
      'Track house point totals and leaderboards',
      'Students and parents receive notifications when awards are granted'
    ],
    contactPrompt: 'To activate the Awards module, please contact us or request a trial from the Modules section.'
  },
  SPORT: {
    title: 'Sport',
    description: 'Manage fixtures, teams, results and availability for all your school sports.',
    features: [
      'Create and manage fixtures against other schools',
      'Build team sheets and send availability requests',
      'Record results and track performance'
    ],
    contactPrompt: 'To activate the Sport module, please contact us or request a trial from the Modules section.'
  },
  CLUBS: {
    title: 'Clubs & Activities',
    description: 'Run after-school clubs, manage bookings and waiting lists.',
    features: [
      'Create clubs with schedules and capacity limits',
      'Let parents enrol or manage bookings by term',
      'Track attendance and send reminders'
    ],
    contactPrompt: 'To activate the Clubs module, please contact us or request a trial from the Modules section.'
  },
  COMMUNICATIONS: {
    title: 'Whole-school Communications',
    description: 'Send messages to any group of parents, students or staff.',
    features: [
      'Target messages by year group, sport, club or house',
      'Attach files and images',
      'Track read receipts and delivery status'
    ],
    contactPrompt: 'To activate Communications, please contact us or request a trial from the Modules section.'
  }
};

const DEFAULT_CONFIG: ModuleUpsellConfig = {
  title: 'Module not active',
  description: 'This feature requires a module that is not currently active for your school.',
  features: [],
  contactPrompt: 'Please contact us or visit the Modules section to activate it.'
};

interface Props {
  isOpen: boolean;
  onClose: () => void;
  module: string;
}

export const ModuleUpsellModal: React.FC<Props> = ({ isOpen, onClose, module }) => {
  const cfg = MODULE_CONFIG[module] ?? DEFAULT_CONFIG;

  const body = (
    <div className="moduleUpsell">
      <div className="moduleUpsell__intro">
        <span className="moduleUpsell__icon" aria-hidden="true">
          <FontAwesomeIcon icon={faLock} />
        </span>
        <p>{cfg.description}</p>
      </div>

      {cfg.features.length > 0 && (
        <ul className="moduleUpsell__features">
          {cfg.features.map((f, i) => (
            <li key={i}>
              <FontAwesomeIcon icon={faCheck} aria-hidden="true" />
              <span>{f}</span>
            </li>
          ))}
        </ul>
      )}

      {cfg.contactPrompt && (
        <div className="moduleUpsell__prompt">{cfg.contactPrompt}</div>
      )}
    </div>
  );

  return (
    <SimpleModal
      isOpen={isOpen}
      title={`${cfg.title} is not active`}
      body={body}
      buttonText="Close"
      onButtonClick={onClose}
      onCloseClick={onClose}
      hideCancelButton
      isBodyBold={false}
      customClass="moduleUpsellModal"
      dialogStyle={{ maxWidth: 520 }}
    />
  );
};
